/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\dashboards\components\AddWidgetModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Tools module.
----------------------------------------------------------------*/


import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Cpu, Activity, Network, Phone, Server, HardDrive, Zap, FileText, Radio } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';

interface WidgetType {
    typeKey: string;
    name: string;
    description: string;
    category: string;
    icon: string;
    defaultConfig: Record<string, any>;
    defaultSize: { w: number; h: number; minW: number; minH: number };
    realTime: boolean;
}

interface AddWidgetModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddWidget: (widgetType: string, title?: string) => void;
    widgetTypes: WidgetType[];
}

const ICON_MAP: Record<string, React.ElementType> = {
    Cpu,
    Activity,
    Network,
    Phone,
    Server,
    HardDrive,
    Zap,
    FileText,
    Radio,
};

const CATEGORY_LABELS: Record<string, string> = {
    system: 'System',
    telephony: 'Telephony',
    admin: 'Administration',
};

export function AddWidgetModal({ isOpen, onClose, onAddWidget, widgetTypes }: AddWidgetModalProps) {
    const { t } = useI18n();
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    if (!isOpen) return null;

    // Filter widget types
    const filteredTypes = widgetTypes.filter(wt => {
        const matchesSearch = !search ||
            wt.name.toLowerCase().includes(search.toLowerCase()) ||
            wt.description.toLowerCase().includes(search.toLowerCase());
        const matchesCategory = !selectedCategory || wt.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    // Get unique categories
    const categories = [...new Set(widgetTypes.map(wt => wt.category))];

    const handleAdd = (widgetType: WidgetType) => {
        onAddWidget(widgetType.typeKey, widgetType.name);
        onClose();
        setSearch('');
        setSelectedCategory(null);
    };

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50"
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }}
            />

            {/* Modal */}
            <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                    <h2 className="text-lg font-semibold text-foreground">Add Widget</h2>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search and Filters */}
                <div className="px-6 py-4 border-b border-border space-y-3">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search widgets..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>

                    {/* Category Filters */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setSelectedCategory(null)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!selectedCategory
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-muted-foreground hover:text-foreground'
                                }`}
                        >
                            All
                        </button>
                        {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${selectedCategory === cat
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                {CATEGORY_LABELS[cat] || cat}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Widget List */}
                <div className="flex-1 overflow-auto p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {filteredTypes.map(wt => {
                            const Icon = ICON_MAP[wt.icon] || Server;

                            return (
                                <button
                                    key={wt.typeKey}
                                    onClick={() => handleAdd(wt)}
                                    className="flex items-start gap-3 p-4 bg-muted/50 hover:bg-muted rounded-lg border border-border hover:border-primary transition-all text-left group"
                                >
                                    <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-foreground">{wt.name}</span>
                                            {wt.realTime && (
                                                <span className="px-1.5 py-0.5 bg-green-500/10 text-green-500 text-[10px] font-medium rounded">
                                                    LIVE
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                            {wt.description}
                                        </p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {filteredTypes.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground">
                            <p className="text-sm">No widgets found</p>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
